import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './StripePayments.css';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_PUBLISHABLE_KEY);

// Setup Form for automatic top-ups
const SetupForm = ({ amount, threshold, onSuccess, onError, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [message, setMessage] = useState('');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: localStorage.getItem('username') || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !isReady) return;

    if (!billingDetails.name.trim()) {
      setMessage('Please enter your name');
      return;
    }

    if (!billingDetails.email.trim()) {
      setMessage('Please enter your email');
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/setup-success`,
          payment_method_data: {
            billing_details: {
              name: billingDetails.name,
              email: billingDetails.email,
            },
          },
        },
      });

      if (error) {
        setMessage(error.message || 'An error occurred during setup');
        setIsProcessing(false);
        if (onError) onError(error);
      } else {
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setMessage('An unexpected error occurred');
      setIsProcessing(false);
      if (onError) onError(err);
    }
  };

  const handleInputChange = (field, value) => {
    setBillingDetails(prev => ({ ...prev, [field]: value }));
    setMessage('');
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="auto-topup-info">
        <h3>Automatic Top-up Settings</h3>
        <div className="info-box">
          <p><strong>Top-up Amount:</strong> £{amount}</p>
          <p><strong>Trigger:</strong> When balance falls below £{threshold}</p>
          <p className="info-note">Your card will be charged automatically when your balance is low.</p>
        </div>
      </div>

      <div className="form-section">
        <h3>Billing Information</h3>

        <div className="form-field">
          <label htmlFor="name">Full Name *</label>
          <input
            id="name"
            type="text"
            className="stripe-input"
            value={billingDetails.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            disabled={isProcessing}
            required
          />
        </div>

        <div className="form-field">
          <label htmlFor="email">Email Address *</label>
          <input
            id="email"
            type="email"
            className="stripe-input"
            value={billingDetails.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={isProcessing}
            required
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Payment Method</h3>
        <PaymentElement
          onReady={() => {
            console.log('PaymentElement is ready!');
            setIsReady(true);
          }}
          onLoadError={(error) => {
            console.error('PaymentElement load error:', error);
            setMessage('Failed to load payment form. Please refresh the page.');
          }}
          options={{ layout: 'tabs' }}
        />
      </div>

      {message && (
        <div className="payment-message error">{message}</div>
      )}

      <div className="form-actions">
        <button
          type="button"
          onClick={onBack}
          className="back-button-inline"
          disabled={isProcessing}
        >
          Back
        </button>
        <button
          type="submit"
          className="pay-button"
          disabled={!stripe || !elements || !isReady || isProcessing}
        >
          {isProcessing ? (
            <><span className="spinner"></span>Setting up...</>
          ) : (
            'Enable Auto Top-up'
          )}
        </button>
      </div>
      {(!stripe || !elements || !isReady) && !isProcessing && (
        <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>
          {!stripe || !elements ? 'Loading payment system...' : 'Loading payment form...'}
        </p>
      )}
    </form>
  );
};

// One-time payment form
const CheckoutForm = ({ amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [message, setMessage] = useState('');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: localStorage.getItem('username') || ''
  });

  useEffect(() => {
    console.log('Stripe loaded:', !!stripe);
    console.log('Elements loaded:', !!elements);
    console.log('Payment Element ready:', isReady);
  }, [stripe, elements, isReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !isReady) return;

    if (!billingDetails.name.trim()) {
      setMessage('Please enter your name');
      return;
    }

    if (!billingDetails.email.trim()) {
      setMessage('Please enter your email');
      return;
    }

    // Save balance snapshot before Stripe navigates away — used by PaymentReturn to detect the update
    const currentCredits = localStorage.getItem('groupCredits');
    if (currentCredits !== null) {
      localStorage.setItem('balanceBeforePayment', currentCredits);
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
          payment_method_data: {
            billing_details: {
              name: billingDetails.name,
              email: billingDetails.email,
            },
          },
        },
      });

      if (error) {
        setMessage(error.message || 'An error occurred during payment');
        setIsProcessing(false);
        if (onError) onError(error);
      } else {
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setMessage('An unexpected error occurred');
      setIsProcessing(false);
      if (onError) onError(err);
    }
  };

  const handleInputChange = (field, value) => {
    setBillingDetails(prev => ({ ...prev, [field]: value }));
    setMessage('');
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="form-section">
        <h3>Billing Information</h3>

        <div className="form-field">
          <label htmlFor="name">Full Name *</label>
          <input
            id="name"
            type="text"
            className="stripe-input"
            value={billingDetails.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            disabled={isProcessing}
            required
          />
        </div>

        <div className="form-field">
          <label htmlFor="email">Email Address *</label>
          <input
            id="email"
            type="email"
            className="stripe-input"
            value={billingDetails.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={isProcessing}
            required
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Payment Details</h3>
        <PaymentElement
          onReady={() => {
            console.log('PaymentElement is ready!');
            setIsReady(true);
          }}
          onLoadError={(error) => {
            console.error('PaymentElement load error:', error);
            setMessage('Failed to load payment form. Please refresh the page.');
          }}
          options={{ layout: 'tabs' }}
        />
      </div>

      {message && (
        <div className="payment-message error">{message}</div>
      )}

      <div className="form-actions">
        <button
          type="submit"
          className="pay-button"
          disabled={!stripe || !elements || !isReady || isProcessing}
        >
          {isProcessing ? (
            <><span className="spinner"></span>Processing...</>
          ) : (
            `Pay £${amount}`
          )}
        </button>
        {(!stripe || !elements || !isReady) && !isProcessing && (
          <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>
            {!stripe || !elements ? 'Loading payment system...' : 'Loading payment form...'}
          </p>
        )}
      </div>
    </form>
  );
};

// Main StripePayment component
const StripePayment = () => {
  const [step, setStep] = useState('type'); // 'type', 'amount', 'payment', 'setup'
  const [paymentType, setPaymentType] = useState(null); // 'one-time' or 'auto-topup'
  const [amount, setAmount] = useState('');
  const [threshold, setThreshold] = useState('10.00');
  const [clientSecret, setClientSecret] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [totalWithTax, setTotalWithTax] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const MINIMUM_AMOUNT = 50;
  const MINIMUM_THRESHOLD = 10;
  const CREDITS_PER_POUND = 100;

  const poundsToCredits = (pounds) => Math.round(parseFloat(pounds) * CREDITS_PER_POUND);

  const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  };

  const handleTypeSelection = (type) => {
    setPaymentType(type);
    setStep('amount');
    setError('');
  };

  const handleAmountSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (amountNum < MINIMUM_AMOUNT) {
      setError(`Minimum purchase amount is £${MINIMUM_AMOUNT}`);
      return;
    }
    if (paymentType === 'auto-topup') {
      const thresholdNum = parseFloat(threshold);
      if (isNaN(thresholdNum) || thresholdNum < MINIMUM_THRESHOLD) {
        setError(`Minimum threshold is £${MINIMUM_THRESHOLD}`);
        return;
      }
    }

    setIsLoading(true);
    try {
      const amountInPence = Math.round(amountNum * 100);
      const thresholdInCredits = poundsToCredits(threshold);
      const username = localStorage.getItem('username');
      const email = localStorage.getItem('username') || '';

      if (paymentType === 'one-time') {
        const data = await fetchWithAuth(
          `${process.env.REACT_APP_FQDN}/api/create-payment-intent`,
          {
            method: 'POST',
            body: JSON.stringify({ amountInPence, metadata: { username } }),
          }
        );
        if (!data.clientSecret) throw new Error('No client secret received from server');
        setClientSecret(data.clientSecret);
        setTotalWithTax(data.total_amount_with_tax ? (data.total_amount_with_tax / 100).toFixed(2) : amountNum.toFixed(2));
        setStep('payment');
      } else {
        const data = await fetchWithAuth(
          `${process.env.REACT_APP_FQDN}/api/create-setup-intent`,
          {
            method: 'POST',
            body: JSON.stringify({ email, topupAmount: amountInPence, threshold: thresholdInCredits, username }),
          }
        );
        if (!data.clientSecret) throw new Error('No setup secret received from server');
        setSetupSecret(data.clientSecret);
        setStep('setup');
      }
    } catch (err) {
      setError(err.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setStep('type');
    setPaymentType(null);
    setClientSecret('');
    setSetupSecret('');
    setTotalWithTax(null);
    setError('');
    setAmount('');
    setThreshold('10.00');
  };

  const handleBackToAmount = () => {
    setStep('amount');
    setClientSecret('');
    setSetupSecret('');
    setError('');
  };

  return (
    <div className="stripe-payment-container">
      <div className="payment-card">
        <h2>Purchase Balance</h2>

        {step === 'type' && (
          <div className="payment-type-selection">
            <p className="section-description">Choose how you'd like to manage your balance:</p>
            <div className="payment-type-options">
              <button className="payment-type-button" onClick={() => handleTypeSelection('one-time')}>
                <div className="payment-type-icon">💳</div>
                <h3>One-time Purchase</h3>
                <p>Add balance now with a single payment</p>
              </button>
              <button className="payment-type-button" onClick={() => handleTypeSelection('auto-topup')}>
                <div className="payment-type-icon">🔄</div>
                <h3>Automatic Top-up</h3>
                <p>Set up automatic payments when your balance runs low</p>
              </button>
            </div>
          </div>
        )}

        {step === 'amount' && (
          <div>
            <button onClick={handleReset} className="back-button">← Back to Options</button>
            <form onSubmit={handleAmountSubmit} className="amount-form">
              <div className="form-field">
                <label htmlFor="amount">
                  {paymentType === 'auto-topup' ? 'Top-up Amount (GBP)' : 'Amount (GBP)'}
                </label>
                <div className="amount-input-wrapper">
                  <span className="currency-symbol">£</span>
                  <input
                    id="amount"
                    type="number"
                    className="amount-input"
                    value={amount}
                    onChange={(e) => { setAmount(e.target.value); setError(''); }}
                    placeholder="50.00"
                    min={MINIMUM_AMOUNT}
                    step="0.01"
                    disabled={isLoading}
                    required
                  />
                </div>
                <small className="field-hint">Minimum amount: £{MINIMUM_AMOUNT}</small>
              </div>

              {paymentType === 'auto-topup' && (
                <div className="form-field">
                  <label htmlFor="threshold">Top-up Trigger (GBP)</label>
                  <div className="amount-input-wrapper">
                    <span className="currency-symbol">£</span>
                    <input
                      id="threshold"
                      type="number"
                      className="amount-input"
                      value={threshold}
                      onChange={(e) => { setThreshold(e.target.value); setError(''); }}
                      placeholder="10.00"
                      min={MINIMUM_THRESHOLD}
                      step="0.01"
                      disabled={isLoading}
                      required
                    />
                  </div>
                  <small className="field-hint">
                    Automatically charge when balance falls below this amount (minimum £{MINIMUM_THRESHOLD})
                  </small>
                </div>
              )}

              {error && <div className="payment-message error">{error}</div>}

              <button type="submit" className="continue-button" disabled={isLoading || !amount}>
                {isLoading ? <><span className="spinner"></span>Loading...</> : 'Continue to Payment'}
              </button>
            </form>
          </div>
        )}

        {step === 'payment' && clientSecret && (
          <div className="payment-step">
            <div className="payment-summary">
              <button onClick={handleBackToAmount} className="back-button">← Change Amount</button>
            </div>
            {!stripePromise ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#e53e3e' }}>
                Error: Issue with payment system. Please try again later.
              </div>
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#0066cc' } } }}>
                <CheckoutForm
                  amount={totalWithTax || parseFloat(amount).toFixed(2)}
                  onSuccess={() => console.log('Payment successful')}
                  onError={(e) => console.error('Payment error:', e)}
                />
              </Elements>
            )}
          </div>
        )}

        {step === 'setup' && setupSecret && (
          <div className="payment-step">
            {!stripePromise ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#e53e3e' }}>
                Error: Stripe not initialized. Check your REACT_APP_STRIPE_API_PUBLISHABLE_KEY
              </div>
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret: setupSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#0066cc' } } }}>
                <SetupForm
                  amount={parseFloat(amount).toFixed(2)}
                  threshold={parseFloat(threshold).toFixed(2)}
                  onSuccess={() => console.log('Setup successful')}
                  onError={(e) => console.error('Setup error:', e)}
                  onBack={handleBackToAmount}
                />
              </Elements>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StripePayment;
